import { useMemo, useState } from "react";
import {
  Number,
  Unit,
  UnitModules,
  AccordionItem,
  Letter,
  Toggle,
  Module,
  ModuleLink,
  Accordion,
} from "./styles";
import { useTheme } from "styled-components";
import LockIcon from "../../assets/icons/icon-cool/outline/Look.svg";
import BookIcon from "../../assets/icons/icon-cool/outline/Book.svg";
import ChevronBottom from "../../assets/icons/icon-cool/outline/Arrows-chevron/Bottom.svg";
import ArrowRight from "../../assets/icons/icon-cool/outline/Arrows-chevron/ArrowRight.svg";
import ChevronTop from "../../assets/icons/icon-cool/outline/Arrows-chevron/Top.svg";
import StartIcon from "../../assets/icons/icon-cool/outline/Star.svg";

import Typography from "../Typography";
import Icon from "../Icon";
import { CourseDto, LabComponent, LabUnit } from "../../types/course.dto";
import slugify from "slugify";

interface Unit {
  course_id: number;
  title: string;
  id: number;
  current: boolean; // You may set this based on your logic
  lock: boolean; // You may set this based on your logic
  components?: {
    id: number;
    title: string;
    completed: boolean;
    current?: boolean;
  }[];
}

interface AccordionItemProps {
  index: number;
  unit: Unit;
  linkComponents?: boolean;
  showNumbers?: boolean;
  onClick?: () => void;
}

interface ComponentProps {
  course: CourseDto;
  currentUnit?: LabUnit;
  currentComponent?: LabComponent;
  linkComponents?: boolean;
  showNumbers?: boolean;
  onClick?: () => void;
}

const getAlphabeticLetter = (number: number): string | null => {
  if (number < 0 || number >= 26) {
    // Handle out-of-range numbers
    return null;
  }

  // ASCII code for 'A' is 65
  const asciiCodeA = 65;

  // Convert the number to its corresponding ASCII code
  const asciiCode = asciiCodeA + number;

  // Convert ASCII code to character
  const letter = String.fromCharCode(asciiCode);

  return letter;
};

const AccordionItemFc = ({
  index,
  unit,
  linkComponents,
  showNumbers = true,
  onClick,
}: AccordionItemProps) => {
  const theme = useTheme() as { [key: string]: { [key: string]: string } };
  const [isOpen, setIsOpen] = useState(unit?.current);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <AccordionItem showNumbers={showNumbers}>
      {showNumbers && <Number>{index + 1}</Number>}

      <Unit>
        <Toggle
          onClick={
            unit.components && unit.components?.length > 0
              ? toggleAccordion
              : () => {}
          }
        >
          <span>
            {unit?.lock ? (
              <Icon icon={LockIcon} size="14px" color="rgba(250, 182, 0, 1)" />
            ) : unit.id === 0 ? (
              <Icon icon={StartIcon} size="14px" color="black" />
            ) : (
              <Icon icon={BookIcon} size="14px" color="black" />
            )}

            <Typography
              as="h3"
              size="sm"
              style={{
                width: "100%",
                fontWeight: !unit?.current ? "400" : "600",
              }}
            >
              {unit?.title}
            </Typography>
          </span>

          {unit.components && unit.components?.length > 0 && (
            <>
              {isOpen ? (
                <Icon icon={ChevronTop} size="14px" color="black" />
              ) : (
                <Icon icon={ChevronBottom} size="14px" color="black" />
              )}
            </>
          )}
        </Toggle>

        {isOpen && (
          <UnitModules>
            {unit.components &&
              unit.components.map((child, index) => {
                const isClickable = linkComponents && !unit?.lock;
                const Component = isClickable ? ModuleLink : Module;
                
                return (
                  <Component
                    key={slugify(`${child?.id}-${child?.title}`, { lower: true })}
                    completed={child?.completed.toString()}
                    current={child?.current?.toString()}
                    onClick={isClickable ? onClick : undefined}
                    to={
                      isClickable
                        ? `/dashboard/mis-cursos/${unit?.course_id}/${unit?.id}/${child?.id}`
                        : undefined
                    }
                  >
                    <Letter>{getAlphabeticLetter(index)}.</Letter>
                    <Typography as="p" size="xs" style={{ width: "100%" }}>
                      {child.title}
                    </Typography>
                    {isClickable && (
                      <Icon
                        icon={ArrowRight}
                        size="14px"
                        color={theme.colors.Primary}
                      />
                    )}
                  </Component>
                );
              })}
</UnitModules>
        )}
      </Unit>
    </AccordionItem>
  );
};

const Component = ({
  onClick,
  course,
  currentUnit,
  currentComponent,
  linkComponents = false,
  showNumbers = true,
}: ComponentProps) => {
  const units = useMemo(() => {
    if (course) {
      return [
        ...course?.labUnit
          .sort((a, b) => a.sort_order - b.sort_order) // Sort units by sort_order
          .map((unit): Unit => {
            return {
              course_id: course?.id,
              title: unit.title,
              id: unit.id,
              current: !!currentUnit && currentUnit?.id === unit?.id, // You may set this based on your logic
              lock: !!currentUnit && currentUnit?.sort_order < unit?.sort_order, // You may set this based on your logic
              components: unit.labComponent
                .sort((a, b) => a.sort_order - b.sort_order) // Sort labComponents by sort_order
                .map((component) => {
                  return {
                    id: component.id,
                    title: component.title,
                    current:
                      !!currentComponent &&
                      currentComponent?.id === component?.id,
                    completed:
                      !!currentComponent &&
                      !!currentUnit &&
                      (currentUnit?.sort_order >= unit?.sort_order ||
                        (currentUnit?.sort_order === unit?.sort_order &&
                          currentComponent?.sort_order >
                            component?.sort_order)),
                  };
                }),
            };
          }),
      ];
    } else {
      return [];
    }
  }, [course, currentUnit, currentComponent]);

  return (
    <Accordion>
      {units.map((unit, index) => (
        <AccordionItemFc
          key={index}
          index={index}
          unit={unit}
          linkComponents={linkComponents}
          showNumbers={showNumbers}
          onClick={onClick}
        />
      ))}
    </Accordion>
  );
};

export default Component;
